import ReportContent from '@app/components/reports/components/report-content';
import ReportMetric from '@app/components/reports/components/report-metric';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import Text from '@shared/components/content/text';
import { ChartConfig, ChartLegend } from '@shared/components/ui/chart';
import supplyFlowDiagram from '../../assets/report-supply-flow.png';
import { useCCFReportResult } from '../../ccf-report-result-context';

export default function SupplyOfPurchasedGoods() {
  const {
    result: { supplyChainLogistics },
  } = useCCFReportResult();

  const { supplyOfPurchasedGoods } = supplyChainLogistics || {};

  const groupedBySupplierType =
    supplyOfPurchasedGoods?.emissionsBySupplierType.reduce(
      (acc, item) => {
        const { type, category, emissionsTonnes } = item;

        if (!acc[type]) {
          acc[type] = { type };
        }

        acc[type][category] = emissionsTonnes;

        return acc;
      },
      {} as Record<string, Record<string, number | string>>
    );

  const chartData = Object.values(groupedBySupplierType || {});

  const chartConfig = {
    Storage: {
      label: 'Storage',
      color: 'var(--chart-2)',
      unit: 'tCO2e',
    },
    'Transportation and distribution': {
      label: 'Transport',
      color: 'var(--chart-3)',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;

  return (
    <ReportContent>
      <>
        <Text variant="reportBody">
          Our platform models your transportation and distribution emissions in
          two distinct stages: the transportation of your purchased goods from
          your suppliers to your sites and the transportation of the products
          you sell from your sites to the end consumer.
          <br />
          <br />
          This first stage covers emissions from the point goods leave your
          supplier’s sites until you receive them, and includes all modes of
          transport and any storage that happens along the route.
        </Text>
        <div className="my-8">
          <img
            src={supplyFlowDiagram}
            alt="Supply flow diagram"
            className="h-auto w-full max-w-full"
          />
        </div>

        <div className="flex space-x-2">
          <ReportMetric
            title="Total weight transported"
            value={supplyOfPurchasedGoods?.totalWeightKiloTonnes ?? 0}
            unit="kt"
            size="md"
            className="flex-1"
          />
          <ReportMetric
            title="% of goods cold-chain"
            value={supplyOfPurchasedGoods?.percentOfGoodsColdChain ?? 0}
            unit="%"
            size="md"
            className="flex-1"
          />
        </div>
        <div className="flex space-x-2">
          <ReportMetric
            title="Emissions from transport"
            value={supplyOfPurchasedGoods?.transportEmissionsTonnes ?? 0}
            unit="tCO2e"
            size="md"
            className="flex-1"
          />
          <ReportMetric
            title="Emissions from storage"
            value={supplyOfPurchasedGoods?.storageEmissionsTonnes ?? 0}
            unit="tCO2e"
            size="md"
            className="flex-1"
          />
        </div>
      </>
      <>
        <Text variant="h3">Emissions per supplier type</Text>
        <div className="mb-2 flex space-x-9">
          {Object.entries(chartConfig).map((category, idx) => {
            return (
              <div key={idx}>
                <ChartLegend
                  name={category[1].label}
                  color={category[1].color}
                />
              </div>
            );
          })}
        </div>
        <SimpleBarChart
          nameKey="type"
          dataKey="emissions"
          chartData={chartData}
          chartConfig={chartConfig}
          className="size-full max-h-[calc(var(--content-title-height)-20rem)]"
          margin={{ top: 50 }}
          YAxisProps={{
            label: {
              value: 'tCO2e',
              position: 'top',
              offset: 30,
            },
          }}
        />
      </>
    </ReportContent>
  );
}
